import React, { useEffect, useMemo, useState } from 'react';
import './TableList.css';
import RefereeFormModal from './RefereeFormModal';
import LiveBalanceBar from './LiveBalanceBar';
import useLiveBalance from './useLiveBalance';

// helper robust pt. boolean (0/1, true/false, '1'/'0', 'true'/'false')
const toBool = (v) => v === true || v === 1 || v === '1' || v === 'true';

export default function RefereesList({ selectedYear, onYearChange, onListUpdate }) {
  const [referees, setReferees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('active-first');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReferee, setEditingReferee] = useState(null);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const token = useMemo(() => localStorage.getItem('jwtToken'), []);
    const {
      balance,
      loading: balanceLoading,
      prevBalance,
      refresh: refreshBalance
    } = useLiveBalance(selectedYear);
  

  const fetchReferees = () => {
    setLoading(true);

    if (!token) {
      setError('Token JWT lipsă. Reautentifică-te.');
      setLoading(false);
      return;
    }

    fetch(`https://www.fro.ro/api/referees/index.php?year=${selectedYear}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Eroare ${res.status}: ${text}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          const list = data.data || [];
          setReferees(list);
          setError(null);
          onListUpdate?.(list);
        } else {
          setError(data.message || 'Eroare la încărcarea arbitrilor');
        }
      })
      .catch((err) => {
        console.error('Eroare fetch arbitri:', err);
        setError(err.message || 'Eroare de conexiune');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReferees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    onListUpdate?.(referees);
  }, [referees, onListUpdate]);

  const handleDelete = (refereeId) => {
    if (!window.confirm('Sigur dorești să ștergi acest arbitru?')) return;

    fetch(`https://www.fro.ro/api/referees/delete.php?id=${refereeId}`, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + token },
    })
      .then(async (res) => {
        if (!res.ok) {
          const t = await res.text();
          throw new Error(`HTTP ${res.status}: ${t}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          setReferees((prev) => prev.filter((r) => r.id !== refereeId));
        } else {
          alert('Eroare la ștergere: ' + (data.message || 'necunoscută'));
        }
      })
      .catch((err) => alert('Eroare la conexiune: ' + err.message));
  };

  const handleLegitimare = async (referee) => {
    if (selectedYear < currentYear) {
      alert('Nu poți activa arbitri pentru un an anterior.');
      return;
    }

    const ok = window.confirm(
      `Vrei să activezi arbitrul ${referee.first_name} ${referee.last_name} pentru ${selectedYear}?`
    );
    if (!ok) return;

    try {
      const res = await fetch(
        'https://www.fro.ro/api/referees/legitimeaza-arbitru.php',
        {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            referee_id: referee.id,
            year: selectedYear,
          }),
        }
      );

      const text = await res.text();
      console.log('STATUS:', res.status);
      console.log('RESPONSE:', text);

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        alert('Răspuns invalid de la server:\n' + text);
        return;
      }

      if (!res.ok) {
        alert(data.message || 'Nu s-a putut activa.');
        return;
      }

      alert(`Activare efectuată. Taxa: ${data.taxa} lei.`);
      fetchReferees();
      refreshBalance();

    } catch (err) {
      alert('Eroare la conexiune: ' + err.message);
    }
  };


  const handleEdit = (referee) => {
    setEditingReferee(referee);
    setShowAddModal(true);
  };

  // === Filtrare + sortare ===
  const filteredReferees = referees.filter((r) =>
    (`${r.first_name || ''} ${r.last_name || ''}`.toLowerCase()).includes(
      searchTerm.toLowerCase()
    )
  );

  const sortedReferees = [...filteredReferees].sort((a, b) => {
    const activeA = toBool(a.is_legitimated);
    const activeB = toBool(b.is_legitimated);

    switch (sortBy) {
      case 'name-az': {
        const nameA = (`${a.last_name || ''} ${a.first_name || ''}`).toLowerCase();
        const nameB = (`${b.last_name || ''} ${b.first_name || ''}`).toLowerCase();
        return nameA.localeCompare(nameB);
      }
      case 'birth-up':
        return (a.birth_year || 0) - (b.birth_year || 0);
      case 'birth-down':
        return (b.birth_year || 0) - (a.birth_year || 0);
      case 'active-first':
        return activeA === activeB ? 0 : activeA ? -1 : 1;
      case 'inactive-first':
        return activeA === activeB ? 0 : activeA ? 1 : -1;
      default:
        return 0;
    }
  });

  if (loading) return <p>Se încarcă arbitrii...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div className="table-list-container">
      <div className="table-list-header">
         <LiveBalanceBar
                   amount={balance}
                   loading={balanceLoading}
                   prevAmount={prevBalance}
                 />
        <div className="sort-bar">
          <label htmlFor="sort-select" className="sort-label">Sortare:</label>
          <select
            id="sort-select"
            className="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name-az">A–Z după nume</option>
            <option value="birth-up">An naștere ↑</option>
            <option value="birth-down">An naștere ↓</option>
            <option value="active-first">Activi întâi</option>
            <option value="inactive-first">Inactivi întâi</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
          <input
            type="text"
            className="search-bar"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            className="add-circle-btn"
            onClick={() => {
              setEditingReferee(null);
              setShowAddModal(true);
            }}
            aria-label="Adaugă arbitru"
          >
            <span className="plus-icon">+</span>
          </button>

          {/* ✅ Selector an: doar curent + următor */}
          <div className="year-selector">
            <label className="year-label">An:</label>
            <select
              className="year-dropdown"
              value={selectedYear}
              onChange={(e) => onYearChange?.(Number(e.target.value))}
            >
              {[currentYear, currentYear + 1].map((y) => (
                <option key={y} value={y}>
                  {y === currentYear ? `${y} (curent)` : `${y} (următor)`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <table className="table-list">
        <thead>
          <tr>
            <th>Nr. legitimație</th>
            <th>Nume</th>
            <th>An naștere</th>
            <th>Email</th>
            <th>Telefon</th>
            <th>Categorie</th>
            <th>Activat</th>
            <th>Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          {sortedReferees.length === 0 ? (
            <tr>
              <td colSpan={8}>Nu există arbitri.</td>
            </tr>
          ) : (
            sortedReferees.map((referee) => (
              <tr
                key={referee.id}
                className={toBool(referee.is_legitimated) ? 'active-row' : ''}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  {referee.license_number ? (
                    <span className="badge-legitimatie">{referee.license_number}</span>
                  ) : (
                    <span className="badge-legitimatie empty">-</span>
                  )}
                </td>

                <td>{referee.first_name} {referee.last_name}</td>
                <td>{referee.birth_year}</td>
                <td>{referee.email || '-'}</td>
                <td>{referee.phone || '-'}</td>
                <td>{referee.categorie || '-'}</td>

                <td>
                  <input
                    type="checkbox"
                    checked={toBool(referee.is_legitimated)}
                    disabled={toBool(referee.is_legitimated) || selectedYear < currentYear - 1}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleLegitimare(referee);
                    }}
                  />
                </td>

                <td className="table-list-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(referee);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(referee.id);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {showAddModal && (
        <RefereeFormModal
          onClose={() => setShowAddModal(false)}
          onRefereeAdded={fetchReferees}
          editData={editingReferee}
        />
      )}
    </div>
  );
}
